import { Dialog, Image, Typography } from '@/components/UI'
import { SwipeButton } from '@/components/UI/SwipeButton'
import { IconLoader2, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** ダイアログの表示状態 */
  opened: boolean
  /** × ボタン・「あとで」ボタン押下時 */
  onClose: () => void
  /** スワイプ完了時（クーポン使用確定 / 商品受け取り確定） */
  onUseCoupon: () => void
  /** クーポン名 / 商品名 */
  couponName: string
  /** クーポン画像URL */
  couponImage?: string | null
  /** 使用期間 開始日（例: "2026/02/01"） */
  usagePeriodFrom?: string | null
  /** 使用期間 終了日（例: "2026/3/31"） */
  usagePeriodTo?: string | null
  /** API通信中フラグ（true のときスワイプボタンをローディング表示に置き換える） */
  isLoading?: boolean
  /** ダイアログヘッダータイトル。省略時は「クーポン使用」 */
  title?: string
  /** 警告文（スタッフへの提示案内）1行目。省略時はクーポン用テキスト */
  warningLine1?: string
  /** 警告文（スタッフへの提示案内）2行目。省略時はクーポン用テキスト */
  warningLine2?: string
  /** スワイプボタンのテキスト。省略時は「▶右にスワイプで使用」 */
  swipeText?: string
  /** 警告文（1行）。指定した場合は warningLine1 / warningLine2 より優先される */
  warningText?: string
  /**
   * 使用スポット名（スポット記録型クーポンのときのみ指定）
   * 指定した場合、couponName の直下に「使用スポット」ラベルとスポット名を subtitle1 フォントで表示する
   */
  spotName?: string | null
}

export const DialogUseCoupon = ({
  opened,
  onClose,
  onUseCoupon,
  couponName,
  couponImage,
  usagePeriodFrom,
  usagePeriodTo,
  isLoading = false,
  title,
  warningLine1,
  warningLine2,
  swipeText,
  warningText,
  spotName,
}: Props) => {
  const { t } = useTranslation()

  return (
    <Dialog selectorState={opened} closeFunc={onClose} showCloseButton={false}>
      <div data-testid="dialog-use-coupon">
        {/* ヘッダー */}
        <div className="relative flex h-[44px] items-center justify-center px-4">
          <h2 className="text-[16px] font-bold leading-[24px] text-black">
            {title ?? t('coupon.use_title', 'クーポン使用')}
          </h2>
          <button
            type="button"
            className="absolute right-0 top-[10px] text-black"
            onClick={onClose}
            aria-label={t('common.close', '閉じる')}
            disabled={isLoading}
          >
            <IconX size={24} stroke={1.5} />
          </button>
        </div>
        {/* クーポン画像 */}
        {couponImage && (
          <div className="aspect-[25/16] overflow-hidden">
            <Image src={couponImage} />
          </div>
        )}

        {/* クーポン名 */}
        <Typography variant="subtitle1" component="p" ta="center" c="black" className="mt-2">
          {couponName}
        </Typography>

        {/* 使用スポット（スポット記録型クーポンのときのみ表示） */}
        {spotName && (
          <div className="mt-4 flex flex-col items-center">
            <Typography variant="subtitle1" component="p" ta="center" c="black">
              {t('coupon.use_spot', '使用スポット')}
            </Typography>
            <Typography variant="subtitle1" component="p" ta="center" c="black">
              {spotName}
            </Typography>
          </div>
        )}

        {/* 警告文 */}
        <Typography
          variant="subtitle1"
          component="p"
          ta="center"
          c="#D46A6A"
          className="mt-3 whitespace-pre-line px-4"
          style={{ fontSize: '14px' }}
        >
          {warningText != null ? (
            warningText
          ) : (
            <>
              {warningLine1 ?? t('coupon.show_to_staff_line1', 'クーポンを使用するときに必ず')}
              <br />
              {warningLine2 ?? t('coupon.show_to_staff_line2', 'スタッフにこの画面を見せてください')}
            </>
          )}
        </Typography>

        {/* 使用期間 */}
        {(usagePeriodFrom || usagePeriodTo) && (
          <div className="mt-2 text-center text-black">
            <Typography variant="caption1" component="p">
              {t('coupon.usage_period', '使用期間')}
            </Typography>
            <Typography variant="caption1" component="p">
              {usagePeriodFrom}〜{usagePeriodTo}
            </Typography>
          </div>
        )}

        {/* スワイプボタン / ローディング */}
        <div className="mt-6 px-4">
          {isLoading ? (
            <div className="flex h-[56px] w-full items-center justify-center rounded-full bg-main">
              <IconLoader2
                className="animate-spin text-black"
                size={24}
                stroke={2}
                aria-label={t('common.loading', '処理中')}
              />
            </div>
          ) : (
            <SwipeButton
              text={swipeText ?? t('coupon.swipe_to_use', '▶右にスワイプで使用')}
              onSwipeDone={onUseCoupon}
              textSize="text-[13px]"
              width="w-full"
              bgColor="bg-main"
              spinnerColor="text-black"
            />
          )}
        </div>

        {/* あとでボタン */}
        <div className="mb-4 mt-4 flex justify-center">
          <button
            type="button"
            className="flex h-[48px] w-[123px] items-center justify-center rounded-full bg-[#EEEEEE] text-[15px] font-bold leading-[24px] text-[#757575] disabled:opacity-50"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('common.later', 'あとで')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
